import React, { useState, useCallback, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import { Excalidraw, exportToBlob } from '@excalidraw/excalidraw';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/apiClient';
import Timer from './Timer';
import SkeletonLoader from './SkeletonLoader';
import './DesignCanvas.css';
// Design canvas component with Excalidraw integration

const DesignCanvas = forwardRef(function DesignCanvas({ selectedQuestion, onChange }, ref) {
  const [excalidrawAPI, setExcalidrawAPI] = useState(null);
  const [isLoadingDesign, setIsLoadingDesign] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [isHoveringHeader, setIsHoveringHeader] = useState(false);
  const [isCanvasFullscreen, setIsCanvasFullscreen] = useState(false);
  const { user } = useAuth();
  const canvasContainerRef = useRef(null);

  const toggleCanvasFullscreen = useCallback(() => {
    if (!canvasContainerRef.current) return;

    if (!document.fullscreenElement) {
      canvasContainerRef.current.requestFullscreen().then(() => {
        setIsCanvasFullscreen(true);
      }).catch(() => {});
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => {
          setIsCanvasFullscreen(false);
        });
      }
    }
  }, []);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsCanvasFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Keyboard shortcut for focus mode (Shift+F)
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Shift+F to toggle focus mode
      if (e.key === 'F' && e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        toggleCanvasFullscreen();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [toggleCanvasFullscreen]);

  // Auto-load latest design when component mounts or question changes
  useEffect(() => {
    if (excalidrawAPI && selectedQuestion && user) {
      loadLatestDesign();
    }
  }, [excalidrawAPI, selectedQuestion?.id, user]);

  const loadLatestDesign = useCallback(async () => {
    if (!selectedQuestion || !user || !excalidrawAPI) return;

    excalidrawAPI.resetScene();

    try {
      setIsLoadingDesign(true);
      const designData = await apiClient.get(`/api/designs/latest?question_id=${selectedQuestion.id}`);

      if (designData && designData.json_data) {
        const jsonData = designData.json_data;

        // Restore the scene
        excalidrawAPI.updateScene({
          elements: jsonData.elements || [],
          appState: {
            ...jsonData.appState,
            viewBackgroundColor: '#ffffff',
          },
        });

        // Scroll to content after a short delay (keep 100% zoom)
        setTimeout(() => {
          if (jsonData.elements && jsonData.elements.length > 0) {
            excalidrawAPI.scrollToContent(jsonData.elements, {
              fitToViewport: false,
              animate: true,
            });
          }
        }, 100);
      }
    } catch {
      // 404 or other - no previous design or load error
    } finally {
      setIsLoadingDesign(false);
    }
  }, [selectedQuestion, user, excalidrawAPI]);

  // Set white background when API is available
  useEffect(() => {
    if (excalidrawAPI) {
      excalidrawAPI.updateScene({
        appState: {
          viewBackgroundColor: '#ffffff',
        },
      });
    }
  }, [excalidrawAPI]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    excalidrawAPI,
    getDesignData: () => {
      if (excalidrawAPI) {
        return {
          elements: excalidrawAPI.getSceneElements(),
          appState: excalidrawAPI.getAppState(),
        };
      }
      return null;
    },
    // Load a specific design into the canvas
    loadDesign: (designData) => {
      if (excalidrawAPI && designData && designData.json_data) {
        const jsonData = designData.json_data;
        excalidrawAPI.updateScene({
          elements: jsonData.elements || [],
          appState: {
            ...jsonData.appState,
            viewBackgroundColor: '#ffffff',
          },
        });

        // Scroll to content
        setTimeout(() => {
          excalidrawAPI.scrollToContent(jsonData.elements || [], {
            fitToViewport: false,
          });
        }, 100);
      }
    },
    // Export as Excalidraw JSON (includes all text, shapes, arrows)
    exportAsJson: () => {
      if (excalidrawAPI) {
        const elements = excalidrawAPI.getSceneElements();
        const appState = excalidrawAPI.getAppState();
        const files = excalidrawAPI.getFiles();

        if (elements.length === 0) {
          return null;
        }

        const excalidrawJson = {
          type: "excalidraw",
          version: 2,
          source: "system-design-lab",
          elements: elements.filter(el => !el.isDeleted),
          appState: {
            viewBackgroundColor: appState.viewBackgroundColor,
          },
          files,
        };


        // Extract text content for easier analysis
        const textElements = elements
          .filter(el => el.type === 'text' && !el.isDeleted)
          .map(el => el.text);

        // Extract shapes/components
        const shapes = elements
          .filter(el => !el.isDeleted && el.type !== 'text')
          .map(el => ({
            type: el.type,
            id: el.id,
            // Include bound text if any
            boundText: el.boundElements?.filter(b => b.type === 'text').map(b => {
              const textEl = elements.find(e => e.id === b.id);
              return textEl?.text;
            }).filter(Boolean)
          }));

        return {
          json: excalidrawJson,
          textLabels: textElements,
          shapes: shapes,
          summary: {
            totalElements: elements.filter(el => !el.isDeleted).length,
            textCount: textElements.length,
            shapeCount: shapes.length
          }
        };
      }
      return null;
    },
    // Export as image using Excalidraw's exportToBlob
    exportAsImage: async () => {
      if (excalidrawAPI) {
        const elements = excalidrawAPI.getSceneElements();
        const appState = excalidrawAPI.getAppState();
        const files = excalidrawAPI.getFiles();

        if (elements.length === 0 || elements.filter(el => !el.isDeleted).length === 0) {
          return null;
        }

        const blob = await exportToBlob({
          elements,
          appState,
          files,
          mimeType: "image/png",
        });

        return blob;
      }
      return null;
    },
    // Export as PNG with base64 data URL for backend
    exportAsPng: async () => {
      if (excalidrawAPI) {
        const elements = excalidrawAPI.getSceneElements();
        const appState = excalidrawAPI.getAppState();
        const files = excalidrawAPI.getFiles();

        if (elements.length === 0 || elements.filter(el => !el.isDeleted).length === 0) {
          return null;
        }

        const blob = await exportToBlob({
          elements,
          appState,
          files,
          mimeType: "image/png",
        });

        // Convert blob to base64 data URL
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
      return null;
    },
    // Export both image and JSON data (JSON for display, image for LLM)
    exportForEvaluation: async () => {
      if (excalidrawAPI) {
        const elements = excalidrawAPI.getSceneElements();
        const appState = excalidrawAPI.getAppState();
        const files = excalidrawAPI.getFiles();

        const activeElements = elements.filter(el => !el.isDeleted);

        if (activeElements.length === 0) {
          return null;
        }

        // Get JSON data (for display/debugging, not sent to LLM)
        const textElements = elements
          .filter(el => el.type === 'text' && !el.isDeleted)
          .map(el => el.text);

        const shapes = elements
          .filter(el => !el.isDeleted && el.type !== 'text')
          .map(el => ({
            type: el.type,
            id: el.id,
            boundText: el.boundElements?.filter(b => b.type === 'text').map(b => {
              const textEl = elements.find(e => e.id === b.id);
              return textEl?.text;
            }).filter(Boolean)
          }));

        const imageBlob = await exportToBlob({
          elements,
          appState,
          files,
          mimeType: "image/png",
        });

        return {
          imageBlob,  // Sent to Gemini
          // JSON data kept for display only (not sent to LLM)
          textLabels: textElements,
          shapes: shapes,
          summary: {
            totalElements: activeElements.length,
            textCount: textElements.length,
            shapeCount: shapes.length
          }
        };
      }
      return null;
    }
  }), [excalidrawAPI]);

  const handleClear = () => {
    if (excalidrawAPI) {
      excalidrawAPI.resetScene();
    }
  };

  const handleExport = useCallback(() => {
    if (excalidrawAPI) {
      const elements = excalidrawAPI.getSceneElements();
      const appState = excalidrawAPI.getAppState();

      const data = {
        elements,
        appState: {
          viewBackgroundColor: appState.viewBackgroundColor,
        },
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'url-shortener-design.excalidraw';
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [excalidrawAPI]);

  return (
    <>
      <div className="canvas-container" ref={canvasContainerRef}>
        <button
          className="canvas-fullscreen-btn"
          onClick={toggleCanvasFullscreen}
          title={isCanvasFullscreen ? 'Exit Focus Mode (ESC)' : 'Focus Mode (Shift+F)'}
        >
          {isCanvasFullscreen ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
            </svg>
          )}
        </button>
        {isLoadingDesign && (
          <div className="canvas-loading-overlay">
            <SkeletonLoader type="canvas" />
          </div>
        )}
        <Excalidraw
          excalidrawAPI={(api) => setExcalidrawAPI(api)}
          onChange={(elements, appState, files) => {
            // Track if user has drawn anything
            const activeElements = elements.filter(el => !el.isDeleted);
            if (activeElements.length > 0 && !hasDrawn) {
              setHasDrawn(true);
            }

            if (onChange) {
              onChange(elements, appState, files);
            }
          }}
          theme="dark"
          initialData={{
            appState: {
              viewBackgroundColor: '#ffffff',
              currentItemFontFamily: 1,
            },
          }}
          UIOptions={{
            canvasActions: {
              loadScene: true,
              export: false,
              saveAsImage: true,
            },
          }}
        />
      </div>
    </>
  );
});

export default DesignCanvas;
